import React, { useEffect, useRef, useCallback, useState } from "react";
import {
  Box,
  List,
  ListItem,
  Typography,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import { useNavigate } from "react-router-dom";
import ChatItem from "./ChatItem";
import useGroupedChats from "./useGroupedChats";

type Chat = {
  id: string;
  name: string;
  created: Date;
  updated: Date;
  messages: { id: number; sender: string; content: string }[];
};

type ChatListProps = {
  activeChat: string | null;
  setActiveChat: (chat: string) => void;
  onEditChat: (chatId: string) => void;
  onFeedbackChat: (chatId: string) => void;
  onDeleteChat: (chatId: string) => void;
  chats: Chat[];
  isGenerating: boolean;
  isLoading: boolean;
  onLoadMore: () => Promise<void>;
  hasMore: boolean;
};

const ChatList: React.FC<ChatListProps> = ({
  activeChat,
  setActiveChat,
  onEditChat,
  onFeedbackChat,
  onDeleteChat,
  chats,
  isGenerating,
  isLoading,
  onLoadMore,
  hasMore,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const lastElementRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const groupedChats = useGroupedChats(chats);

  // Handle initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Check if we have a user session
        const userSession = localStorage.getItem("userSession");
        if (userSession) {
          // Load initial chats if not already loaded
          if (chats.length === 0 && !isLoading) {
            await onLoadMore();
          }
        }
      } catch (error) {
        console.error("Error loading initial chats:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const handleChatClick = useCallback(
    (chatId: string, isAction: boolean = false) => {
      if (!isAction) {
        setActiveChat(chatId);
        navigate(`/chat/${chatId}`);
      }
    },
    [navigate, setActiveChat]
  );

  const handleScroll = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (
        entry.isIntersecting &&
        hasMore &&
        !isLoading &&
        !loadingRef.current
      ) {
        loadingRef.current = true;
        try {
          await onLoadMore();
        } finally {
          loadingRef.current = false;
        }
      }
    },
    [hasMore, isLoading, onLoadMore]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleScroll, {
      root: null,
      rootMargin: "100px",
      threshold: 0,
    });

    const currentElement = lastElementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [handleScroll]);

  // Show loading state during initial load
  if (initialLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        sx={{ height: "100%" }}
      >
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ mt: 2 }}>
          กำลังโหลดประวัติการสนทนา...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        "&::-webkit-scrollbar": {
          width: "0.4em",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: `${theme.palette.text.primary}22`,
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: `${theme.palette.text.primary}44`,
        },
        scrollbarColor: `${theme.palette.text.primary}22`,
      }}
    >
      {chats.length > 0 ? (
        <>
          {Object.entries(groupedChats).map(([category, categoryChats]) => (
            <Box key={category}>
              <ListItem>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "text.primary",
                  }}
                >
                  {category}
                </Typography>
              </ListItem>
              <List>
                {categoryChats.map((chat) => (
                  <ChatItem
                    key={chat.id}
                    chat={chat}
                    activeChat={activeChat}
                    setActiveChat={handleChatClick}
                    onEditChat={onEditChat}
                    onFeedbackChat={onFeedbackChat}
                    onDeleteChat={onDeleteChat}
                    isGenerating={isGenerating}
                  />
                ))}
              </List>
            </Box>
          ))}
          <div
            ref={lastElementRef}
            style={{ height: "40px", padding: "10px 0" }}
          >
            {isLoading && (
              <LinearProgress
                color="primary"
                sx={{ width: "80%", borderRadius: "8px", margin: "16px auto" }}
              />
            )}
          </div>
        </>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          sx={{ height: "100%", opacity: 0.6 }}
        >
          <SmsOutlinedIcon sx={{ fontSize: 18, color: "text.primary" }} />
          <Typography variant="body2" color="text.primary" sx={{ m: 2 }}>
            {isLoading ? "กำลังโหลดประวัติการสนทนา..." : "ไม่พบประวัติการสนทนา"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ChatList;
